/* eslint-disable no-console */
import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';

type SentryConfig = {
  applicationName: string;
  denyUrls?: Array<string | RegExp>;
  dsn?: string;
  sourceVersion?: string;
  version?: string;
};

/**
 * Provides Sentry error handling and tracing for Angular applications.
 *
 * Note: Sentry itself must still be enabled in your own code. For example:
 *
 * ```typescript
 * import { enableSentryLogging } from '@ichoosr-core/logging';
 * import version from './version.json';
 *
 * const config = ...; // Get your config from somewhere
 *
 * enableSentryLogging({
 *  applicationName: 'my-app',
 *  dsn: config.SENTRY_DSN,
 *  sourceVersion: config.SOURCE_VERSION,
 *  version: version.version,
 * });
 * ```
 * @returns EnvironmentProviders
 */
export function provideSentry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Per the Sentry docs. This presumably ensures the trace service is initialized before the app starts.
      inject(TraceService);
    }),
  ]);
}

/**
 * Enables Sentry logging for the application. If no DSN is provided, enabling Sentry will be skipped.
 * @param applicationName - The name of the application.
 * @param denyUrls - URL patterns of scripts whose errors should not be sent to Sentry.
 * @param dsn - The Sentry DSN (Data Source Name) for logging.
 * @param sourceVersion - The source version (e.g., commit hash) of the application.
 * @param version - The version of the application (e.g., '1.0.0').
 */
export function enableSentryLogging({
  applicationName,
  denyUrls,
  dsn,
  sourceVersion,
  version,
}: SentryConfig): void {
  if (dsn) {
    // Use provided version or default to '1.0.0'
    const appVersion = version || '1.0.0';

    Sentry.init({
      dsn,
      sampleRate: 1.0,
      integrations: [
        Sentry.httpClientIntegration({
          failedRequestStatusCodes: [
            [400, 499],
            [500, 599],
          ],
        }),
      ],
      sendDefaultPii: true,
      release: `${applicationName}@${appVersion}+${sourceVersion || 'no-hash'}`,
      denyUrls,
    });
  } else {
    console.warn('Sentry DSN not provided, continuing without logging.');
  }
}

/**
 * Captures an error that was handled in application code (for example in a store's `catchError`).
 * HTTP errors are skipped, since `httpClientIntegration` already reports failed requests.
 */
export function captureHandledError(
  error: unknown,
  context: { action?: string; fieldName?: string } = {},
): void {
  if (error instanceof HttpErrorResponse) {
    return;
  }

  Sentry.captureException(error, {
    level: 'warning',
    tags: { handled: 'true', error_action: context.action, field_name: context.fieldName },
  });
}
